import { useCallback, useEffect, useState } from 'react';
import macName from '../lib/macName';
import { Link } from 'react-router-dom';
import { bytes } from './ModelSpecs';

// where a model of yours lives: this copy, the macs that have it, a cloud run; and the
// ways to keep it training from here. owner only.

const API = '/api';
const LIVE = 'rgb(77, 255, 133)';

function headers() {
  const h = { 'Content-Type': 'application/json' };
  const t = localStorage.getItem('logos_session_token');
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
}

function Small({ children, onClick, disabled, primary }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`border px-3 py-1 rounded-full font-mono text-xs lowercase transition-all disabled:opacity-40 ${
        primary ? 'border-white bg-white text-black hover:bg-transparent hover:text-white' : 'border-[#383f38] text-white hover:bg-white hover:text-black'}`}>
      {children}
    </button>
  );
}

export default function ModelWhereabouts({ checkpointId }) {
  const [where, setWhere] = useState(null);
  const [note, setNote] = useState(null);

  const load = useCallback(() => fetch(`${API}/me/models/${checkpointId}/where`, { headers: headers(), credentials: 'include' })
    .then((r) => (r.ok ? r.json() : null)).then(setWhere), [checkpointId]);
  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);

  const ask = async (mac, kind) => {
    const res = await fetch(`${API}/me/apps/${mac.token_id}/commands`, {
      method: 'POST', headers: headers(), credentials: 'include',
      body: JSON.stringify({ kind, args: { checkpoint: mac.local_name } }),
    });
    const body = await res.json().catch(() => ({}));
    setNote(res.ok ? (kind === 'train.resume' ? `asked ${macName(mac.name)} to continue training it` : `asked ${macName(mac.name)} to back it up here`)
      : body.detail || 'the app could not be asked');
    setTimeout(load, 3000);
  };

  if (!where) return null;
  const here = where.workspace?.bytes_seen || 0;
  return (
    <div data-testid="model-whereabouts">
      <p className="text-[#888888] lowercase text-sm mb-3">where it lives</p>
      <div className="divide-y divide-[#1a201b] border-y border-[#1a201b] font-mono text-sm">
        <div className="py-2.5 flex items-center gap-3 flex-wrap">
          <span className="text-white lowercase">your workspace</span>
          <span className="text-xs text-[#888888]">this copy · {bytes(here)} read</span>
        </div>
        {where.macs.map((mac) => {
          const ahead = (mac.bytes_seen || 0) > here;
          return (
            <div key={mac.token_id} className="py-2.5 flex items-center gap-3 flex-wrap">
              <span className="w-2 h-2 rounded-full" style={{ background: mac.online ? LIVE : '#444444' }} />
              <Link to={`/remote/${mac.token_id}`} className="text-white lowercase hover:underline underline-offset-4 decoration-[#444444]">{macName(mac.name)}</Link>
              <span className="text-xs text-[#888888]">
                {mac.local_name} · {mac.bytes_seen ? `${bytes(mac.bytes_seen)} read` : 'size unknown'}
                {mac.training ? ' · training now' : ''}{ahead && !mac.training ? ' · ahead of this copy' : ''}
                {!mac.online ? ' · offline' : ''}
              </span>
              <span className="ml-auto flex gap-2">
                {!mac.training && (
                  <Small primary onClick={() => ask(mac, 'train.resume')} disabled={!mac.online || mac.busy}>continue on this mac</Small>
                )}
                {mac.chat_ok && (
                  <Link to={`/chat?mac=${mac.token_id}&model=${encodeURIComponent(mac.local_name)}`}><Small>chat</Small></Link>
                )}
                {ahead && <Small onClick={() => ask(mac, 'model.backup')} disabled={!mac.online}>back up to here</Small>}
              </span>
            </div>
          );
        })}
        <div className="py-2.5 flex items-center gap-3 flex-wrap">
          <span className="text-white lowercase">the cloud</span>
          {where.cloud ? (
            <Link to={`/runs/${where.cloud.run_id}`} className="text-xs" style={{ color: LIVE }}>
              {where.cloud.status} on {where.cloud.gpu_name} →
            </Link>
          ) : where.cloud_training ? (
            <span className="ml-auto"><Link to={`/train?model=${checkpointId}`}><Small>continue in the cloud</Small></Link></span>
          ) : (
            <span className="text-xs text-[#666666] lowercase">cloud training opens soon</span>
          )}
        </div>
      </div>
      {where.macs.length === 0 && (
        <p className="font-mono text-[11px] text-[#555555] lowercase mt-2">
          no mac of yours reports this model. a mac that has it shows here once its soma app is signed in.
        </p>
      )}
      {note && <p className="font-mono text-xs text-white lowercase mt-2">{note}</p>}
    </div>
  );
}
